import { Loader2, X } from "lucide-react";

export interface EditNoteModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  isSaving: boolean;
  isLoading: boolean;
  title: string;
  content: string;
  onTitleChange: (value: string) => void;
  onContentChange: (value: string) => void;
}

export function EditNoteModal({
  open,
  onClose,
  onSave,
  isSaving,
  isLoading,
  title,
  content,
  onTitleChange,
  onContentChange,
}: Readonly<EditNoteModalProps>) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center z-50 p-4">
      <div className="bg-surface-primary border border-border-primary rounded-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border-primary">
          <h2 className="text-lg font-semibold text-content-primary">Edit Note</h2>
          <button onClick={onClose} className="p-1 text-content-tertiary hover:text-content-primary transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center p-8">
            <Loader2 className="h-6 w-6 animate-spin text-brand-primary" />
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-auto p-4 space-y-4">
              <div>
                <label htmlFor="edit-note-title" className="block text-sm font-medium text-content-secondary mb-1.5">
                  Title
                </label>
                <input
                  id="edit-note-title"
                  type="text"
                  value={title}
                  onChange={(e) => onTitleChange(e.target.value)}
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
                />
              </div>
              <div>
                <label htmlFor="edit-note-content" className="block text-sm font-medium text-content-secondary mb-1.5">
                  Content
                </label>
                <textarea
                  id="edit-note-content"
                  rows={12}
                  value={content}
                  onChange={(e) => onContentChange(e.target.value)}
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary resize-none font-mono text-sm"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t border-border-primary">
              <button
                onClick={onClose}
                disabled={isSaving}
                className="px-4 py-2 text-content-secondary hover:text-content-primary transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                disabled={isSaving || !title.trim() || !content.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                {isSaving ? "Saving..." : "Save & Re-index"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
